import { mapState } from "vuex";
import * as apis from "@/api";

export default {
  data() {
    return {
      // step1
      boxName: "",
      // step2
      caseImgList: [],
      selectedStyle: null,
      loading: false,
      // step3 默认区间固定最低1%最高2%，滑块选一个值
      defaultRangeValue: 1,
      rangeFillPercent: "50%", // 与浏览器滑点位置对齐的填充百分比
      rangeSliderResizeObserver: null,
      // step4 选择物品
      minprice: "",
      maxprice: "",
      sortRange: "default",
      searchKeyword: "",
      itemCategory: "all",
      itemList: [],
      selectedItems: [],
      itemsLoading: false,
      // pagination for items
      page: 1,
      pageSize: 22,
      totalItems: 0,
      // 物品分类来自接口 GET /UserCase/CaseItemType
      itemCategories: [{ label: "全部", value: "all" }],
      // step5 概率列表（与 selectedItems 同步，可编辑概率）
      probabilityList: [],
      // 创建盲盒配置（来自 GET /api/UserCase/Config）
      userCaseConfig: null,
      // 开箱售价与收益（来自 POST api/UserCase/CalculatePrice）
      boxSalePrice: "—",
      boxProfit: "—",
      calculatePriceTimer: null,
    };
  },
  computed: {
    ...mapState(["user"]),
    vipLevelText() {
      const cfg = this.userCaseConfig;
      if (cfg && cfg.vipLevel != null) return "VIP" + cfg.vipLevel;
      return this.user?.vipLevel != null ? "VIP" + this.user.vipLevel : "VIP0";
    },
    createLimit() {
      const cfg = this.userCaseConfig;
      if (cfg && cfg.maxCaseCount != null) return cfg.maxCaseCount;
      return this.user?.createCaseLimit ?? 5;
    },
    remainCreate() {
      const cfg = this.userCaseConfig;
      if (cfg && cfg.remainingCount != null) return cfg.remainingCount;
      return this.user?.remainCreateCount ?? 5;
    },
    createdCount() {
      const cfg = this.userCaseConfig;
      if (cfg && cfg.createdCount != null) return cfg.createdCount;
      return (this.user?.createCaseLimit ?? 5) - (this.user?.remainCreateCount ?? 5);
    },
    vipMaxValue() {
      const cfg = this.userCaseConfig;
      if (cfg && cfg.maxItemPrice != null) return cfg.maxItemPrice;
      return this.user?.vipMaxItemValue ?? 200;
    },
    minDefaultRate() {
      const cfg = this.userCaseConfig;
      const v = cfg?.minDefaultRate;
      return v != null ? Number(v) : 1;
    },
    maxDefaultRate() {
      const cfg = this.userCaseConfig;
      const v = cfg?.maxDefaultRate;
      return v != null ? Number(v) : 2;
    },
    rangeStep() {
      const min = this.minDefaultRate;
      const max = this.maxDefaultRate;
      const range = max - min;
      if (range <= 0) return 0.1;
      return range <= 10 ? 0.1 : 1;
    },
    filteredItemList() {
      return this.itemList;
    },
    totalProbability() {
      return this.probabilityList.reduce((sum, row) => sum + (Number(row.probability) || 0), 0);
    },
  },
  watch: {
    defaultRangeValue() {
      this.$nextTick(() => this.updateRangeSliderFill());
      this.scheduleCalculatePrice();
    },
    probabilityList: {
      handler() {
        this.scheduleCalculatePrice();
      },
      deep: true,
    },
    selectedItems: {
      handler(items) {
        this.probabilityList = items.map((item) => ({
          ...item,
          name: item.name || item.itemName,
          price: item.price ?? 0,
          probability: item.probability ?? "",
        }));
      },
      deep: true,
    },
    itemCategory() {
      this.page = 1;
      this.fetchItemList();
    },
    searchKeyword() {
      this.page = 1;
      this.fetchItemList();
    },
    sortRange() {
      this.page = 1;
      this.fetchItemList();
    },
  },
  async mounted() {
    if (!this.user || !this.user.nickname) {
      this.$store.commit("setloginModal", true);
    }
    await this.fetchUserCaseConfig();
    await this.fetchCaseImgList();
    await this.fetchCaseItemType();
    await this.fetchItemList();
    this.$nextTick(() => {
      this.updateRangeSliderFill();
      const wrap = this.$refs.rangeSliderWrap;
      if (wrap && typeof ResizeObserver !== "undefined") {
        this.rangeSliderResizeObserver = new ResizeObserver(() => this.updateRangeSliderFill());
        this.rangeSliderResizeObserver.observe(wrap);
      }
    });
  },
  beforeDestroy() {
    if (this.rangeSliderResizeObserver) {
      this.rangeSliderResizeObserver.disconnect();
    }
    if (this.calculatePriceTimer) {
      clearTimeout(this.calculatePriceTimer);
    }
  },
  methods: {
    async fetchUserCaseConfig() {
      try {
        const res = await apis.getUserCaseConfig();
        if (res.data && (res.data.code === 0 || res.data.success) && res.data.data) {
          this.userCaseConfig = res.data.data;
          const cfg = this.userCaseConfig;
          const min = cfg?.minDefaultRate != null ? Number(cfg.minDefaultRate) : 1;
          const max = cfg?.maxDefaultRate != null ? Number(cfg.maxDefaultRate) : 2;
          const val = Number(this.defaultRangeValue);
          if (val < min || val > max) {
            this.defaultRangeValue = min <= max ? (min + max) / 2 : min;
          }
        }
      } catch (e) {
        console.error("getUserCaseConfig error", e);
      }
    },
    async fetchCaseImgList() {
      this.loading = true;
      try {
        const res = await apis.getCaseImgList();
        if (res.data && (res.data.code === 0 || res.data.success) && res.data.data) {
          let raw = res.data.data;
          const arr = Array.isArray(raw) ? raw : (raw.list || raw.items || []);
          // 接口可能直接返回图片 URL 字符串数组，规范为 { id, imgUrl, casePic, picture }
          const list = arr.map((item, index) =>
            typeof item === "string"
              ? { id: index, imgUrl: item, casePic: item, picture: item }
              : item
          );
          this.caseImgList = list;
          if (list.length && !this.selectedStyle) this.selectedStyle = list[0];
        }
      } catch (e) {
        console.error("getCaseImgList error", e);
      } finally {
        this.loading = false;
      }
    },
    updateRangeSliderFill() {
      const wrap = this.$refs.rangeSliderWrap;
      if (!wrap) return;
      const input = wrap.querySelector('input[type="range"]');
      if (!input) return;
      const min = this.minDefaultRate;
      const max = this.maxDefaultRate;
      const value = Number(this.defaultRangeValue);
      const thumbWidth = 16;
      const trackWidth = input.offsetWidth;
      if (trackWidth <= 0) return;
      const range = max - min;
      const ratio = range <= 0 ? 0 : (value - min) / range;
      const thumbCenterPx = ratio * (trackWidth - thumbWidth) + thumbWidth / 2;
      const fillPercent = (thumbCenterPx / trackWidth) * 100;
      this.rangeFillPercent = fillPercent + "%";
    },
    onSelectStyle(item) {
      this.selectedStyle = item;
    },
    onBack() {
      this.$router.replace({ name: "boxDiy" });
    },
    async fetchCaseItemType() {
      try {
        const res = await apis.getCaseItemType();
        if (res.data && res.data.code === 0 && res.data.data) {
          const raw = Array.isArray(res.data.data) ? res.data.data : (res.data.data.list || res.data.data.items || []);
          const list = raw.map((item) => ({
            label: item.label ?? item.name ?? item.typeName ?? item.value ?? "",
            value: item.value ?? item.id ?? item.typeId ?? item.name ?? "",
          })).filter((item) => item.label && item.value);
          this.itemCategories = [{ label: "全部", value: "all" }, ...list];
        }
      } catch (e) {
        console.error("getCaseItemType error", e);
      }
    },
    onSearchPrice() {
      this.page = 1;
      this.fetchItemList();
    },
    handlePageChange(val) {
      this.page = val;
      this.fetchItemList();
    },
    async fetchItemList() {
      this.itemsLoading = true;
      try {
        const cat = this.itemCategory === "all" ? 0 : this.itemCategory;
        const minprice = this.minprice !== "" && this.minprice !== null ? Number(this.minprice) : 0;
        const maxprice = this.maxprice !== "" && this.maxprice !== null ? Number(this.maxprice) : 999999;
        const res = await apis.getCaseItemList({
          cat,
          minprice,
          maxprice,
          sort: this.sortRange === "default" ? "" : this.sortRange,
          keyword: this.searchKeyword,
          page: this.page,
          pageSize: this.pageSize
        });
        if (res.data && (res.data.code === 0 || res.data.success) && res.data.data) {
          const raw = res.data.data;
          this.totalItems = raw.total || 0;
          this.page = raw.page || this.page;
          const arr = Array.isArray(raw) ? raw : (raw.list || raw.items || []);
          // 接口返回 { id, name, imgUrl, rarity, rarityName, price }，规范为模板使用的字段
          const list = arr.map((item) => ({
            ...item,
            itemId: item.itemId ?? item.id,
            itemName: item.itemName ?? item.name,
            itemPic: item.itemPic ?? item.imgUrl ?? item.picture,
            picture: item.picture ?? item.imgUrl,
          }));
          this.itemList = list;
        }
      } catch (e) {
        console.log("getCaseItemList error", e);
        this.itemList = [];
      } finally {
        this.itemsLoading = false;
      }
    },
    isItemSelected(item) {
      const id = item.id ?? item.itemId;
      return this.selectedItems.some((s) => (s.id ?? s.itemId) === id);
    },
    toggleSelectItem(item) {
      const id = item.id ?? item.itemId;
      const idx = this.selectedItems.findIndex((s) => (s.id ?? s.itemId) === id);
      if (idx >= 0) {
        this.selectedItems.splice(idx, 1);
        return;
      }
      const price = Number(item.price);
      const maxVal = Number(this.vipMaxValue);
      if (!isNaN(maxVal) && !isNaN(price) && price > maxVal) {
        const msg = `当前VIP等级可选饰品的最高价值为 ${maxVal}，该物品价格 ${price} 已超过限制`;
        this.$toast && this.$toast.fail ? this.$toast.fail(msg) : (this.$message && this.$message.warning(msg));
        return;
      }
      this.selectedItems.push({ ...item });
    },
    removeProbabilityRow(index) {
      this.selectedItems.splice(index, 1);
    },
    autoDistributeProb() {
      const list = this.probabilityList;
      if (!list.length) return;
      const lastIndex = list.length - 1;
      const sumOthers = list.slice(0, lastIndex).reduce((s, r) => s + (Number(r.probability) || 0), 0);
      if (sumOthers >= 100) {
        this.$toast && this.$toast.fail
          ? this.$toast.fail("前面概率总和已超过或等于100%，请先调整")
          : (this.$message && this.$message.warning("前面概率总和已超过或等于100%，请先调整"));
        return;
      }
      const lastVal = 100 - sumOthers;
      if (lastVal < 1 || lastVal > 100) {
        this.$toast && this.$toast.fail
          ? this.$toast.fail("最后一项无法补足为1-100，请调整前面概率")
          : (this.$message && this.$message.warning("最后一项无法补足为1-100，请调整前面概率"));
        return;
      }
      this.$set(list[lastIndex], "probability", lastVal);
    },
    normalizeProbabilities() {
      const total = this.probabilityList.reduce((s, r) => s + (Number(r.probability) || 0), 0);
      if (total === 0 || total === 100) return;
      const scale = 100 / total;
      this.probabilityList.forEach((row) => {
        row.probability = Math.round((Number(row.probability) || 0) * scale);
      });
    },
    scheduleCalculatePrice() {
      if (this.calculatePriceTimer) clearTimeout(this.calculatePriceTimer);
      this.calculatePriceTimer = setTimeout(() => {
        this.fetchCalculatePrice();
        this.calculatePriceTimer = null;
      }, 300);
    },
    async fetchCalculatePrice() {
      if (!this.probabilityList || this.probabilityList.length < 2 || this.totalProbability !== 100) {
        this.boxSalePrice = "—";
        this.boxProfit = "—";
        return;
      }
      const defaultRate = Number(this.defaultRangeValue) || 0;
      const items = this.probabilityList.map((row) => ({
        itemId: Number(row.id ?? row.itemId ?? 0),
        price: Number(row.price) || 0,
        dropRate: Number(row.probability) || 0,
      }));
      //this.$message && this.$message.info && this.$message.info("计算开箱收益");
      try {
        const res = await apis.calculatePrice({ defaultRate, items });
        const ok = res?.data?.code === 0 || res?.data?.success === true;
        const data = res?.data?.data ?? res?.data;
        if (ok && data != null) {
          // 接口返回：price 售价，profitPerOpen 每次开箱收益
          const price = data.price;
          const profitPerOpen = data.profitPerOpen;
          this.boxSalePrice = price != null ? String(Number(price).toFixed(2)) : "—";
          this.boxProfit = profitPerOpen != null ? String(Number(profitPerOpen).toFixed(2)) : "—";
        } else {
          this.boxSalePrice = "—";
          this.boxProfit = "—";
          const errMsg = res?.data?.msg || res?.data?.message || "计算失败";
          this.$message && this.$message.error && this.$message.error(errMsg);
        }
      } catch (e) {
        console.error("calculatePrice error", e);
        this.boxSalePrice = "—";
        this.boxProfit = "—";
        const errMsg = e?.response?.data?.msg || e?.response?.data?.message || "计算失败，请稍后重试";
        this.$message && this.$message.error && this.$message.error(errMsg);
      }
    },
    async onConfirm() {
      if (!this.user || !this.user.nickname) {
        return this.$store.commit("setloginModal", true);
      }
      const name = (this.boxName || "").trim();
      if (!name) {
        this.$toast && this.$toast.fail ? this.$toast.fail("请输入箱子名称") : (this.$message && this.$message.warning("请输入箱子名称"));
        return;
      }
      if (!this.selectedStyle) {
        this.$toast && this.$toast.fail ? this.$toast.fail("请选择箱子样式") : (this.$message && this.$message.warning("请选择箱子样式"));
        return;
      }
      if (!this.probabilityList.length) {
        this.$toast && this.$toast.fail ? this.$toast.fail("请至少选择一个物品并设置概率") : (this.$message && this.$message.warning("请至少选择一个物品并设置概率"));
        return;
      }
      if (this.totalProbability !== 100) {
        this.$toast && this.$toast.fail ? this.$toast.fail("概率总和必须为100%") : (this.$message && this.$message.warning("概率总和必须为100%"));
        return;
      }
      const caseImgUrl = this.selectedStyle.imgUrl || this.selectedStyle.casePic || this.selectedStyle.picture || "";
      const defaultRate = Number(this.defaultRangeValue);
      const items = this.probabilityList.map((row) => ({
        itemId: Number(row.id ?? row.itemId ?? 0),
        dropRate: Number(row.probability) || 0,
      }));
      try {
        const res = await apis.createUserCase({ name, caseImgUrl, defaultRate, items });
        if (res.data && res.data.code === 0) {
          this.$toast && this.$toast.success ? this.$toast.success("已创建成功，请等待后台审核") : (this.$message && this.$message.success("创建成功"));
          this.$router.replace({ name: "boxDiy" });
        } else {
          const msg = (res.data && res.data.msg) || "创建失败";
          this.$toast && this.$toast.fail ? this.$toast.fail(msg) : (this.$message && this.$message.error(msg));
        }
      } catch (e) {
        console.error("createUserCase error", e);
        const msg = (e.response && e.response.data && e.response.data.msg) || e.message || "创建失败";
        this.$toast && this.$toast.fail ? this.$toast.fail(msg) : (this.$message && this.$message.error(msg));
      }
    },
  },
};
